if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector("form");

        const selectedVariantId =
          this.form?.querySelector(".product-variant-id") ||
          this.form?.querySelector("input[name='id']");
        if (selectedVariantId) selectedVariantId.disabled = false;

        this.cart = null; // We don't need theme cart rendering since we use Monster Cart
        //this.cart = document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector("cart-drawer"))
          this.submitButton?.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

        // Internal state for dynamic render resiliency
        this._observer = null;
        this._initialized = false;
        this._lastAttachedNodes = { form: null, submit: null };
        this._handlers = {};
      }

      connectedCallback() {
        // connected

        // Robust init: query and attach, then observe for dynamic changes
        this._tryQueryElements();
        this._ensureListeners();
        // Debug interaction taps removed after validation
        this._attachGlobalSubmitFallback();
        this._startObserving();
      }

      disconnectedCallback() {
        if (this._observer) {
          this._observer.disconnect();
          this._observer = null;
        }
      }

      /**
       * Query for form and submit button with resilient selectors. Idempotent.
       */
      _tryQueryElements() {
        const foundForm =
          this.querySelector('form[action*="/cart/add" i]') ||
          this.querySelector("form");
        if (foundForm && this.form !== foundForm) {
          this.form = foundForm;
        }

        const foundSubmit = this.querySelector(
          'button[type="submit"], [type="submit"]'
        );
        if (foundSubmit && this.submitButton !== foundSubmit) {
          this.submitButton = foundSubmit;
        }
      }

      /**
       * Ensure event listeners are attached to current form/button. Idempotent.
       */
      _ensureListeners() {
        if (this.form && this.form !== this._lastAttachedNodes.form) {
          if (!this.form._submitListenerAttached) {
            this.form.addEventListener(
              "submit",
              this.onSubmitHandler.bind(this),
              true
            );
            this.form._submitListenerAttached = true;
          }
          this._lastAttachedNodes.form = this.form;
        }

        if (
          this.submitButton &&
          this.submitButton !== this._lastAttachedNodes.submit
        ) {
          if (!this.submitButton._clickListenerAttached) {
            this.submitButton.addEventListener(
              "click",
              this.onSubmitHandler.bind(this)
            );
            this.submitButton._clickListenerAttached = true;
          }
          this._lastAttachedNodes.submit = this.submitButton;
        }
      }

      /**
       * Observe DOM changes within this element to re-query and re-attach when content is re-rendered.
       */
      _startObserving() {
        if (this._observer) return;
        this._observer = new MutationObserver((mutations) => {
          const relevant = mutations.some((m) => m.type === "childList");
          if (!relevant) return;
          this._tryQueryElements();
          this._ensureListeners();
        });
        this._observer.observe(this, { childList: true, subtree: true });
      }

      /**
       * Attach extra debug listeners to understand interaction flow on the submit button and form.
       */
      // Debug interaction taps removed

      /**
       * Capture-phase fallback: if third-party stops propagation before our listeners,
       * intercept clicks on the submit button and invoke onSubmitHandler directly.
       */
      _attachGlobalSubmitFallback() {
        if (this._handlers.globalClickFallbackAttached) return;
        const handler = (e) => {
          // Only handle primary clicks on our submit button or its descendants
          const target = e.target;
          const isInsideSubmit =
            this.submitButton &&
            (target === this.submitButton ||
              this.submitButton.contains(target));
          if (!isInsideSubmit) return;
          // Avoid re-entry if our handler already ran (spinner visible or aria-disabled set)
          const isLoading = this.submitButton?.classList.contains("loading");
          if (isLoading) return;
          try {
            this.onSubmitHandler(e);
          } catch (err) {
            /* no-op */
          }
        };
        // Capture to run before other bubbling listeners
        this.addEventListener("click", handler, true);
        this._handlers.globalClickFallbackAttached = true;
        this._handlers.globalClickFallback = handler;
      }

      onSubmitHandler(evt) {
        // submit

        evt.preventDefault();

        const ariaDisabled = this.submitButton.getAttribute("aria-disabled");
        if (ariaDisabled === "true") {
          return;
        }

        // Prevent Monster Upsells from intercepting this form
        evt.stopImmediatePropagation();

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        const loadingSpinner = this.querySelector(".loading__spinner");
        if (loadingSpinner) {
          loadingSpinner.classList.remove("hidden");
        }

        const alternateAddToCartButton = document.querySelector(
          ".add-to-cart--alternate"
        );
        if (alternateAddToCartButton) {
          alternateAddToCartButton.setAttribute("aria-disabled", true);
          alternateAddToCartButton.classList.add("loading");
          alternateAddToCartButton
            .querySelector(".loading__spinner")
            ?.classList.remove("hidden");
          alternateAddToCartButton
            .querySelector("span")
            ?.classList.add("hidden");
        }

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        // Create a clean FormData object
        const formData = new FormData();

        // Get the main product quantity
        const quantityInput = this.form.querySelector(
          'input[name="quantity"]'
        );
        const mainProductQuantity = quantityInput?.value || "1";

        // Add main product with consistent format
        const variantIdInput = this.form.querySelector(".product-variant-id");
        const mainProductId = variantIdInput?.value;

        if (!mainProductId) return;

        // Extra lines from blocks that ride along with Add to Cart (the free gift). Each one
        // exposes getCartItems(mainQuantity) and owns its own markup, so this form never reads it.
        // A contributor only applies to forms for its own product (data-variant-ids), so the PDP
        // and its sticky bar include the gift but related-product cards on the same page do not.
        let index = 0;
        document.querySelectorAll("[data-atc-contributor]").forEach((contributor) => {
          if (typeof contributor.getCartItems !== "function") return;
          const variantIds = (contributor.dataset.variantIds || "").split(",");
          if (!variantIds.includes(String(mainProductId))) return;
          contributor.getCartItems(mainProductQuantity).forEach((item) => {
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][quantity]`, item.quantity);
            Object.entries(item.properties || {}).forEach(([key, value]) => {
              formData.append(`items[${index}][properties][${key}]`, value);
            });
            index++;
          });
        });

        // Main product goes last: Shopify's add-to-cart tracking event (GA4 via server-side GTM,
        // Meta, TikTok) only reports the last line of a multi-line add, so this keeps the tracked
        // item the product being bought rather than its free gift.
        formData.append(`items[${index}][id]`, mainProductId);
        formData.append(`items[${index}][quantity]`, mainProductQuantity);

        // ready to submit

        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        window.aoCartActions.run(() => fetch(`${routes.cart_add_url}`, config)
          .then((response) => {
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId:
                  this.form.querySelector(".product-variant-id")?.value ||
                  formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);
              
              const soldOutMessage = this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;

              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } 
            
            if (!this.error)
              // publish(PUB_SUB_EVENTS.cartUpdate, {
              //   source: "product-form",
              //   productVariantId:
              //     this.form.querySelector(".product-variant-id")?.value ||
              //     formData.get("id"),
              //   cartData: response,
              // });
              this.error = false;

            
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              //this.cart.renderContents(response);
              if (typeof openCartDrawer === 'function') {
                openCartDrawer({ type: 'atc', source: 'product_form' });
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");
            const alternateAddToCartButton = document.querySelector(
              ".add-to-cart--alternate"
            );
            if (alternateAddToCartButton) {
              alternateAddToCartButton.classList.remove("loading");
              alternateAddToCartButton.removeAttribute("aria-disabled");
              alternateAddToCartButton
                .querySelector("span")
                .classList.remove("hidden");
              alternateAddToCartButton
                .querySelector(".loading__spinner")
                .classList.add("hidden");
            }
          }));
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
} else {
  // already registered
}
